import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { API } from '../lib/api';
import { useAuth } from './AuthContext';

const CloudConfigContext = createContext(null);

export const CloudConfigProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [config, setConfig] = useState({
    enabled_providers: [],
    accounts: { aws: [], azure: [], gcp: [] },
    loading: true,
    error: null
  });

  const fetchConfig = async () => {
    try {
      const [configResponse, accountsResponse] = await Promise.all([
        axios.get(`${API}/cloud/config`),
        axios.get(`${API}/cloud/accounts`)
      ]);
      
      setConfig({
        enabled_providers: configResponse.data.enabled_providers || [],
        accounts: accountsResponse.data.by_provider || { aws: [], azure: [], gcp: [] },
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching cloud config:', error);
      setConfig(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load cloud configuration'
      }));
    }
  };

  useEffect(() => {
    // Skip the fetch for anonymous visitors on public marketing pages — the
    // backend would just return 401 and pollute the browser console.
    if (authLoading) return;
    if (!isAuthenticated) {
      setConfig(prev => ({ ...prev, loading: false }));
      return;
    }
    fetchConfig();
  }, [authLoading, isAuthenticated]);

  const updateEnabledProviders = async (newProviders) => {
    try {
      await axios.post(`${API}/cloud/config`, { enabled_providers: newProviders });
      setConfig(prev => ({
        ...prev,
        enabled_providers: newProviders
      }));
      return true;
    } catch (error) {
      console.error('Error updating cloud config:', error);
      return false;
    }
  };

  const isProviderEnabled = (provider) => {
    return config.enabled_providers.includes(provider);
  };

  const getConnectedAccountsCount = (provider) => {
    return (config.accounts[provider] || []).length;
  };

  const getTotalConnectedAccounts = () => {
    return Object.values(config.accounts).flat().length;
  };

  const hasAnyEnabledProvider = () => {
    return config.enabled_providers.length > 0;
  };

  const value = {
    ...config,
    fetchConfig,
    updateEnabledProviders,
    isProviderEnabled,
    getConnectedAccountsCount,
    getTotalConnectedAccounts,
    hasAnyEnabledProvider
  };

  return (
    <CloudConfigContext.Provider value={value}>
      {children}
    </CloudConfigContext.Provider>
  );
};

export const useCloudConfig = () => {
  const context = useContext(CloudConfigContext);
  if (!context) {
    throw new Error('useCloudConfig must be used within a CloudConfigProvider');
  }
  return context;
};

export default CloudConfigContext;
